import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Calendar, Grid3x3, LayoutGrid, BarChart3, Clock, ShieldCheck, Layers } from "lucide-react";

/**
 * Picker dialog that lets the user choose how the roster grid is rendered.
 * The 7 layouts below are inspired by the most-used scheduling/roster patterns
 * we found across modern workforce-management products.
 */

const LAYOUTS = [
  {
    id: "week",
    name: "Week",
    tag: "Classic",
    icon: Calendar,
    description: "Employees down the side, Mon → Sun across the top. Best for week-by-week planning.",
    accent: "#002FA7",
  },
  {
    id: "month",
    name: "Month",
    tag: "Calendar",
    icon: LayoutGrid,
    description: "A traditional month-view calendar with shift dots on each day. Great for spotting patterns.",
    accent: "#7C3AED",
  },
  {
    id: "full",
    name: "Full grid",
    tag: "Power user",
    icon: Grid3x3,
    description: "All employees × every date in one scrollable grid. Most information density.",
    accent: "#0891B2",
  },
  {
    id: "timeline",
    name: "Timeline",
    tag: "Gantt-style",
    icon: Clock,
    description: "Hour-of-day time bars per employee. Perfect for visualising overlaps and gaps in coverage.",
    accent: "#059669",
  },
  {
    id: "heatmap",
    name: "Coverage heatmap",
    tag: "Analytics",
    icon: BarChart3,
    description: "Shifts × days, colour-coded by how well-staffed each slot is vs. min/max capacity.",
    accent: "#DC2626",
  },
  {
    id: "daycards",
    name: "Day cards",
    tag: "Kanban",
    icon: Layers,
    description: "One day at a time, with each shift as a vertical column of employee cards. Very hand-edit friendly.",
    accent: "#EA580C",
  },
  {
    id: "police",
    name: "Police HWP",
    tag: "Industry-specific",
    icon: ShieldCheck,
    description: "NSW Police Highway Patrol fortnight layout — AM/PM split cells, REST/A-L pills, and operations strip.",
    accent: "#1E3A8A",
  },
];

const RosterLayoutPicker = ({ open, onOpenChange, value, onChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="layout-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight font-bold">
            Choose a roster layout
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4 -mt-2">
          Pick a display style. You can switch back at any time — it doesn't change your data.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {LAYOUTS.map((layout) => {
            const Icon = layout.icon;
            const active = layout.id === value;
            return (
              <button
                key={layout.id}
                onClick={() => {
                  onChange(layout.id);
                  onOpenChange(false);
                }}
                data-testid={`layout-option-${layout.id}`}
                className={`text-left rounded border p-4 transition-all hover:shadow-md hover:-translate-y-px ${
                  active
                    ? "ring-2 ring-offset-2 ring-offset-white dark:ring-offset-zinc-900 border-transparent"
                    : "border-zinc-200 dark:border-zinc-800"
                }`}
                style={{ "--ring-color": layout.accent, ringColor: layout.accent }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: layout.accent + "1A", color: layout.accent }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                        {layout.name}
                      </span>
                      <span
                        className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: layout.accent + "1A", color: layout.accent }}
                      >
                        {layout.tag}
                      </span>
                      {active && (
                        <span className="text-[10px] uppercase tracking-wide bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                          Selected
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 leading-relaxed">
                      {layout.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ROSTER_LAYOUTS = LAYOUTS;
export default RosterLayoutPicker;
